// ══════════════════════════════════════════════════════════════════
//  ED CRM - Helpers de Comissão (cálculo client-side puro)
// ══════════════════════════════════════════════════════════════════
//  Espelha a lógica do trigger fn_create_commission_on_won (Config/26)
//  para uso no simulador e nas previews da UI. NÃO PERSISTE — só
//  calcula a partir de inputs.
//
//  Fonte de taxas: config.commission_rates (jsonb por workspace).
//  Estrutura esperada:
//   {
//     closer_rates: { '1','6','12','18','bp' -> number },
//     sdr_rates:    { 'perpetuo','imersao','ss' -> number },
//     closer_bonus: [{ min, val }, ...],
//     sdr_bonus:    [{ min, val }, ...],
//     turbo_multiplier: number  -- default 1.5
//   }
// ══════════════════════════════════════════════════════════════════

import type { CommissionRates, BonusTier } from '@/types/domain';

export const PARCEL_LABELS: Record<string, string> = {
  '1':  'PIX (à vista)',
  '6':  '6x',
  '12': '12x',
  '18': '18x',
  'bp': 'BP',
};

export const PARCEL_KEYS = ['1', '6', '12', '18', 'bp'] as const;
export type ParcelKey = typeof PARCEL_KEYS[number];

export const MEETING_TYPE_LABELS: Record<string, string> = {
  perpetuo: 'Perpétuo',
  imersao:  'Imersão',
  ss:       'Social Selling',
};

export const MEETING_TYPE_KEYS = ['perpetuo', 'imersao', 'ss'] as const;
export type MeetingTypeKey = typeof MEETING_TYPE_KEYS[number];

// Taxas default — fonte autoritativa: ED_CRM_v11_Documentacao.docx, seção 5.
// O seed em Config/26 está com bônus Closer = SDR (legado); a UI usa estes
// valores enquanto o gestor não personalizar via Settings → Comissões.
//
// closer_as_sdr_rate: adicional fixo de 1.5% pago ao Closer quando ele
// também atuou como SDR (doc seção 5.4) — independe do meeting_type.
export const DEFAULT_RATES: CommissionRates = {
  closer_rates: { '1': 0.025, '6': 0.0225, '12': 0.02, '18': 0.0175, 'bp': 0.012 },
  sdr_rates:    { perpetuo: 0.015, imersao: 0.010, ss: 0.025 },
  closer_bonus: [
    { min: 100000, val: 500  },
    { min: 150000, val: 1000 },
    { min: 200000, val: 1500 },
    { min: 250000, val: 2000 },
    { min: 300000, val: 3000 },
  ],
  sdr_bonus: [
    { min: 100000, val: 400  },
    { min: 150000, val: 800  },
    { min: 200000, val: 1200 },
    { min: 250000, val: 1600 },
    { min: 300000, val: 2000 },
  ],
  // Doc seção 5.4: "Venda Turbo: comissão base duplicada (≤24h)".
  turbo_multiplier: 2,
  // Doc seção 5.4: "Closer como SDR: +1,5% adicional sobre faturamento".
  closer_as_sdr_rate: 0.015,
};

export interface CommissionInput {
  faturamento?: number | string | null;
  parcela?: string | number | null;
  meeting_type?: string | null;
  is_turbo?: boolean | null;
  closer_as_sdr?: boolean | null;
}

export interface CommissionResult {
  closer_rate: number;
  sdr_rate: number;
  closer_as_sdr_extra_rate: number;
  closer_commission: number;
  sdr_commission: number;
  turbo_multiplier_applied: number;
  closer_as_sdr_applied: boolean;
}

// ─── Mapeamento de parcela ──────────────────────────────────────
//
// Aceita string '1','6','12','18','bp' OU número de parcelas ('3','24',...)
// Retorna a chave canônica usada em CLOSER_RATES.
// Espelha getParcelKey() do legado em js/helpers.js:83-90.
export const getParcelKey = (cond: string | number | null | undefined): ParcelKey => {
  if (cond === 'bp' || cond === 'BP') return 'bp';
  const n = parseInt(String(cond), 10);
  if (Number.isNaN(n)) return '12';
  if (n <= 1)  return '1';
  if (n <= 6)  return '6';
  if (n <= 12) return '12';
  return '18';
};

// ─── Cálculo de comissão de uma venda ────────────────────────────
//
// Regras (doc v11 seção 5):
//   1) Comissão Closer base = faturamento × closer_rate(parcela).
//   2) Se closer_as_sdr=true:
//        - Closer recebe ADICIONAL FIXO de 1.5% sobre faturamento
//          (NÃO a sdr_rate do meeting_type completa).
//        - SDR não recebe — não há SDR separado nesse caso.
//   3) Se closer_as_sdr=false:
//        - SDR recebe faturamento × sdr_rate(meeting_type).
//   4) Turbo (≤24h): comissão base DUPLICADA (×2) — SÓ no Closer.
//        SDR distinto NUNCA recebe turbo (regra de negócio confirmada).
export const calcCommission = (
  input: CommissionInput | null | undefined,
  rates: CommissionRates = DEFAULT_RATES,
): CommissionResult => {
  const fat   = Number(input?.faturamento) || 0;
  const parc  = getParcelKey(input?.parcela);
  const mtype = input?.meeting_type || 'perpetuo';
  const turbo = !!input?.is_turbo;
  const cAsSdr = !!input?.closer_as_sdr;

  // O gating de turbo (inclusive a regra de imersão: só no mesmo dia e sem
  // entrada) é responsabilidade do SaleModal/RPC. Aqui o helper apenas
  // respeita o flag is_turbo recebido.

  const closerRates = rates?.closer_rates || DEFAULT_RATES.closer_rates;
  const sdrRates    = rates?.sdr_rates    || DEFAULT_RATES.sdr_rates;
  const turboMult   = Number(rates?.turbo_multiplier) || 2;
  const cAsSdrRate  = Number(rates?.closer_as_sdr_rate) || 0.015;

  const closerRate = Number(closerRates[parc]) || 0;
  const sdrRate    = Number(sdrRates[mtype])   || 0;

  let closerCommission = fat * closerRate;
  let sdrCommission    = 0;

  if (cAsSdr) {
    // Closer também atuou como SDR — adicional FIXO de 1.5% sobre faturamento.
    closerCommission += fat * cAsSdrRate;
    // Sem SDR separado.
  } else {
    // SDR distinto recebe pela taxa do meeting_type.
    sdrCommission = fat * sdrRate;
  }

  // Turbo: dobra SOMENTE a comissão Closer. SDR distinto nunca recebe turbo.
  if (turbo) {
    closerCommission *= turboMult;
  }

  return {
    closer_rate:               closerRate,
    sdr_rate:                  sdrRate,
    closer_as_sdr_extra_rate:  cAsSdr ? cAsSdrRate : 0,
    closer_commission:         round2(closerCommission),
    sdr_commission:            round2(sdrCommission),
    turbo_multiplier_applied:  turbo ? turboMult : 1,
    closer_as_sdr_applied:     cAsSdr,
  };
};

// ─── Ticket médio ────────────────────────────────────────────────
export const calcTicketMedio = (faturamento: number | string | null | undefined, qtd: number): number =>
  (qtd > 0 ? (Number(faturamento) || 0) / qtd : 0);

// ─── Bônus por faturamento agregado ──────────────────────────────
//
// total: faturamento agregado do período (BRL)
// scale: array [{min, val}] ordenada por min crescente
//
// Retorna o val da maior faixa cuja `min` foi atingida; 0 se nenhuma.
export const getBonusForFaturamento = (
  total: number | string | null | undefined,
  scale: BonusTier[] = [],
): number => {
  if (!Array.isArray(scale) || scale.length === 0) return 0;
  const t = Number(total) || 0;
  const sorted = [...scale].sort((a, b) => (a.min || 0) - (b.min || 0));
  let bonus = 0;
  for (const tier of sorted) {
    if (t >= (tier.min || 0)) bonus = Number(tier.val) || 0;
    else break;
  }
  return bonus;
};

// ─── Próxima faixa de bônus (para "faltam R$ X para R$ Y") ───────
export const getNextBonusTier = (
  total: number | string | null | undefined,
  scale: BonusTier[] = [],
): (BonusTier & { missing: number }) | null => {
  if (!Array.isArray(scale) || scale.length === 0) return null;
  const t = Number(total) || 0;
  const sorted = [...scale].sort((a, b) => (a.min || 0) - (b.min || 0));
  for (const tier of sorted) {
    if (t < (tier.min || 0)) {
      return { ...tier, missing: tier.min - t };
    }
  }
  return null; // já atingiu a faixa máxima
};

export interface PeriodParts {
  year: number;
  month: number;
}

export interface PeriodRange {
  start_iso: string;
  end_iso: string;
  start_date: string;
  end_date: string;
}

export interface ProRata {
  day_of_month: number;
  days_in_month: number;
  ratio: number;
}

// ─── Período (YYYY-MM) ────────────────────────────────────────────
export const getPeriodKey = (date: Date | string | number = new Date()): string => {
  const d = date instanceof Date ? date : new Date(date);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  return `${y}-${m}`;
};

export const parsePeriodKey = (key: string | null | undefined): PeriodParts | null => {
  if (typeof key !== 'string' || !/^\d{4}-\d{2}$/.test(key)) return null;
  const [y, m] = key.split('-').map(Number);
  return { year: y, month: m };
};

export const formatPeriodLabel = (key: string | null | undefined): string => {
  const p = parsePeriodKey(key);
  if (!p) return key || '';
  const months = ['Jan','Fev','Mar','Abr','Mai','Jun','Jul','Ago','Set','Out','Nov','Dez'];
  return `${months[p.month - 1]}/${p.year}`;
};

export const getPeriodRange = (key: string | null | undefined): PeriodRange | null => {
  const p = parsePeriodKey(key);
  if (!p) return null;
  const start = new Date(p.year, p.month - 1, 1);
  const end   = new Date(p.year, p.month, 1);   // exclusivo
  return {
    start_iso: start.toISOString(),
    end_iso:   end.toISOString(),
    start_date: toYMD(start),
    end_date:   toYMD(new Date(end.getTime() - 86400000)), // último dia do mês inclusivo
  };
};

export const shiftPeriod = (key: string | null | undefined, deltaMonths: number): string => {
  const p = parsePeriodKey(key);
  if (!p) return key || '';
  const d = new Date(p.year, p.month - 1 + deltaMonths, 1);
  return getPeriodKey(d);
};

// ─── Pró-rata (% do mês decorrido) ───────────────────────────────
//
// retorna: { day_of_month, days_in_month, ratio }
// ratio é 0.0 a 1.0 (no dia 1 = 1/30 ≈ 0.033, no último dia = 1.0).
export const getMonthProRata = (
  periodKey: string | null | undefined,
  today: Date | string | number = new Date(),
): ProRata => {
  const p = parsePeriodKey(periodKey);
  if (!p) return { day_of_month: 0, days_in_month: 0, ratio: 0 };

  const t = today instanceof Date ? today : new Date(today);
  const tYear = t.getFullYear();
  const tMonth = t.getMonth() + 1;

  const daysInMonth = new Date(p.year, p.month, 0).getDate();

  // Mês passado → 100%; mês futuro → 0%; mês corrente → dia/total.
  if (p.year < tYear || (p.year === tYear && p.month < tMonth)) {
    return { day_of_month: daysInMonth, days_in_month: daysInMonth, ratio: 1 };
  }
  if (p.year > tYear || (p.year === tYear && p.month > tMonth)) {
    return { day_of_month: 0, days_in_month: daysInMonth, ratio: 0 };
  }

  const day = t.getDate();
  return {
    day_of_month: day,
    days_in_month: daysInMonth,
    ratio: day / daysInMonth,
  };
};

// ─── Util ────────────────────────────────────────────────────────
const round2 = (n: number | string | null | undefined): number =>
  Math.round((Number(n) || 0) * 100) / 100;

const toYMD = (d: Date): string => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};
